import React, { useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { Box, Typography, IconButton, Tooltip, Chip, Skeleton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { THEME_COLORS } from '../../shared/constants/theme';
import { getDashboardDarkSurfaceTokens } from '../../utils/dashboardSurfaceSx';
import { sortRatePlans } from '../../utils/ratePlanSort';
import ChannelImage from '../common/ChannelImage';

const ACCENT = THEME_COLORS.PRIMARY;
const ROOM_COL_WIDTH = 280;
const CHANNEL_COL_WIDTH = 228;
const ROOM_ROW_HEIGHT = 92;
const RATE_ROW_HEIGHT = 48;
const CHANNEL_HEAD_HEIGHT = 132;
const CHANNEL_SCROLLBAR_SIZE = 8;

const CHANNEL_STRIP = {
  agoda: '#5B2C6F',
  airbnb: '#FF5A5F',
  'booking.com': '#003580',
  booking: '#003580',
  goibibo: '#F15A22',
  makemytrip: '#E73C40',
  mmt: '#E73C40',
  cleartrip: '#F36F21',
  expedia: '#F5C518',
  easemytrip: '#1B75BB',
};

function channelStripColor(name) {
  const key = String(name || '').toLowerCase().replace(/[^a-z0-9.]/g, '');
  if (CHANNEL_STRIP[key]) return CHANNEL_STRIP[key];
  const match = Object.keys(CHANNEL_STRIP).find((item) => key.includes(item.replace('.', '')));
  return match ? CHANNEL_STRIP[match] : ACCENT;
}

function channelScrollPaneSx(isDark, line) {
  return {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: `${isDark ? 'rgba(255,255,255,0.28)' : 'rgba(15,23,42,0.28)'} transparent`,
    '&::-webkit-scrollbar': {
      height: CHANNEL_SCROLLBAR_SIZE,
      width: CHANNEL_SCROLLBAR_SIZE,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 8,
      backgroundColor: isDark ? 'rgba(255,255,255,0.28)' : 'rgba(15,23,42,0.28)',
    },
    borderLeft: `1px solid ${line}`,
  };
}

function roomSlug(roomType) {
  return roomType.channel_room_code || roomType.code || '';
}

function roomTypeCount(roomType) {
  const count = Number(roomType?.total_rooms ?? roomType?.total_rooms_count);
  return Number.isFinite(count) && count >= 0 ? count : null;
}

function CountChip({ label, icon, accent = false, isDark }) {
  return (
    <Chip
      size="small"
      icon={icon}
      label={label}
      sx={{
        height: 22,
        maxWidth: '100%',
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: 0.1,
        bgcolor: accent
          ? alpha(ACCENT, isDark ? 0.18 : 0.12)
          : isDark
            ? alpha('#fff', 0.06)
            : '#f1f5f9',
        color: accent
          ? (isDark ? ACCENT : '#047857')
          : (isDark ? '#cbd5e1' : '#475569'),
        border: `1px solid ${
          accent
            ? alpha(ACCENT, isDark ? 0.4 : 0.32)
            : isDark
              ? alpha('#fff', 0.1)
              : '#e2e8f0'
        }`,
        '& .MuiChip-icon': {
          fontSize: 13,
          ml: '6px',
          color: 'inherit',
        },
        '& .MuiChip-label': { px: 0.7 },
      }}
    />
  );
}

function mappingIsActive(mapping) {
  return mapping?.is_active !== false;
}

function allMappingsActive(rows = []) {
  return rows.length > 0 && rows.every(mappingIsActive);
}

function MappingStatusButton({ active, disabled, onToggle, title }) {
  const label = title || (active ? 'Disable this mapping' : 'Enable this mapping');
  return (
    <Tooltip title={label}>
      <span onClick={(event) => event.stopPropagation()}>
        <IconButton
          size="small"
          disabled={disabled}
          aria-label={label}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onToggle(!active);
          }}
          sx={{
            width: 28,
            height: 28,
            p: 0,
            color: active ? '#ef4444' : ACCENT,
            '&:hover': {
              bgcolor: alpha(active ? '#ef4444' : ACCENT, 0.14),
            },
            '&.Mui-disabled': { color: alpha('#9ca3af', 0.5) },
          }}
        >
          {active
            ? <RemoveCircleIcon sx={{ fontSize: 22 }} />
            : <AddCircleIcon sx={{ fontSize: 22 }} />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function RateRowSkeleton({ isDark, line }) {
  return (
    <Box
      sx={{
        height: RATE_ROW_HEIGHT,
        px: 1.25,
        display: 'flex',
        alignItems: 'center',
        borderBottom: `1px solid ${alpha(line, 0.6)}`,
        backgroundColor: isDark ? alpha('#fff', 0.02) : '#f8fafb',
      }}
    >
      <Skeleton variant="rounded" height={14} width="72%" />
    </Box>
  );
}

export function ChannelMappingBoardSkeleton({ isDark }) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  const surface = isDark ? tokens.surface : '#fff';
  const line = tokens.surfaceBorder;
  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'hidden',
        border: `1px solid ${line}`,
        borderRadius: 2,
        backgroundColor: isDark ? alpha('#fff', 0.02) : '#f4f6f8',
        minHeight: 360,
      }}
    >
      <Box
        sx={{
          width: ROOM_COL_WIDTH,
          flexShrink: 0,
          backgroundColor: surface,
        }}
      >
        <Box sx={{ height: CHANNEL_HEAD_HEIGHT, px: 2, py: 1.75, borderBottom: `1px solid ${line}` }}>
          <Skeleton variant="text" width={110} height={28} />
          <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
            <Skeleton variant="rounded" width={72} height={22} />
            <Skeleton variant="rounded" width={88} height={22} />
          </Box>
        </Box>
        {[0, 1, 2, 3].map((row) => (
          <Box
            key={row}
            sx={{
              height: ROOM_ROW_HEIGHT,
              px: 2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 0.75,
              borderBottom: `1px solid ${alpha(line, 0.85)}`,
            }}
          >
            <Skeleton variant="text" width="70%" height={20} />
            <Skeleton variant="rounded" width={90} height={20} />
          </Box>
        ))}
      </Box>
      <Box sx={channelScrollPaneSx(isDark, line)}>
      {[0, 1, 2].map((col) => (
        <Box
          key={col}
          sx={{
            width: CHANNEL_COL_WIDTH,
            flexShrink: 0,
            backgroundColor: surface,
            borderRight: `1px solid ${line}`,
          }}
        >
          <Box
            sx={{
              height: CHANNEL_HEAD_HEIGHT,
              px: 1.5,
              pt: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              borderBottom: `1px solid ${line}`,
            }}
          >
            <Skeleton variant="circular" width={42} height={42} />
            <Skeleton variant="text" width={90} height={18} />
            <Skeleton variant="text" width={60} height={14} />
          </Box>
          {[0, 1, 2, 3].map((row) => (
            <Box
              key={row}
              sx={{
                height: ROOM_ROW_HEIGHT,
                px: 1.25,
                display: 'flex',
                alignItems: 'center',
                borderBottom: `1px solid ${alpha(line, 0.85)}`,
              }}
            >
              <Skeleton variant="rounded" height={40} width="100%" />
            </Box>
          ))}
        </Box>
      ))}
      </Box>
    </Box>
  );
}

export default function ChannelMappingBoard({
  isDark,
  roomTypes,
  roomPlansById,
  loadingRoomIds,
  channels,
  mappings,
  pendingMappingIds,
  onExpandRoom,
  onOpenChannel,
  onToggleMappings,
  onDeleteChannel,
}) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  const [expandedRooms, setExpandedRooms] = useState({});

  const boardChannels = useMemo(() => {
    const byId = new Map();
    (channels || []).filter((channel) => !channel.is_deleted).forEach((channel) => {
      byId.set(channel.id, {
        id: channel.id,
        name: channel.name,
        type: channel.channel_type,
        type_display: channel.channel_type_display || channel.channel_type,
        image_path: channel.image_path || channel.channel_image_path || '',
        mappings: [],
      });
    });
    (mappings || []).forEach((mapping) => {
      if (!byId.has(mapping.channel_id)) {
        byId.set(mapping.channel_id, {
          id: mapping.channel_id,
          name: mapping.channel_name,
          type: mapping.channel_type,
          type_display: mapping.channel_type_display || mapping.channel_type,
          image_path: mapping.channel_image_path || '',
          mappings: [],
        });
      }
      byId.get(mapping.channel_id).mappings.push(mapping);
    });
    return Array.from(byId.values()).sort((left, right) => String(left.name).localeCompare(String(right.name)));
  }, [channels, mappings]);

  const loadingRoomSet = useMemo(
    () => new Set((loadingRoomIds || []).map(String)),
    [loadingRoomIds]
  );

  const roomRows = useMemo(
    () =>
      (roomTypes || []).map((roomType) => {
        const key = String(roomType.id);
        const loadedPlans = roomPlansById?.[key];
        return {
          roomType,
          plans: sortRatePlans(loadedPlans || []),
          plansLoaded: Array.isArray(loadedPlans),
          plansLoading: loadingRoomSet.has(key),
          ratePlanCount: Array.isArray(loadedPlans)
            ? loadedPlans.length
            : Number(roomType.rate_plan_count || 0),
        };
      }),
    [loadingRoomSet, roomPlansById, roomTypes]
  );

  useEffect(() => {
    Object.entries(expandedRooms).forEach(([roomTypeId, open]) => {
      if (!open) return;
      const key = String(roomTypeId);
      if (Array.isArray(roomPlansById?.[key]) || loadingRoomSet.has(key)) return;
      onExpandRoom?.(roomTypeId);
    });
  }, [expandedRooms, loadingRoomSet, onExpandRoom, roomPlansById]);

  const totalRatePlans = useMemo(
    () => roomRows.reduce((sum, row) => sum + row.ratePlanCount, 0),
    [roomRows]
  );

  const mappingsByChannelRoom = useMemo(() => {
    const index = {};
    (mappings || []).forEach((mapping) => {
      const key = `${mapping.channel_id}:${mapping.room_type_id}`;
      if (!index[key]) index[key] = [];
      index[key].push(mapping);
    });
    return index;
  }, [mappings]);

  const surface = isDark ? tokens.surface : '#fff';
  const muted = tokens.subtleText;
  const text = isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT;
  const line = tokens.surfaceBorder;

  const pendingIds = useMemo(
    () => new Set([...(pendingMappingIds || [])].map(String)),
    [pendingMappingIds]
  );

  const isPending = (rows = []) => rows.some((row) => pendingIds.has(String(row.id)));

  const toggleRoom = (roomTypeId, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const key = String(roomTypeId);
    setExpandedRooms((prev) => {
      const nextOpen = !prev[key];
      if (nextOpen) onExpandRoom?.(roomTypeId);
      return { ...prev, [key]: nextOpen };
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'hidden',
        border: `1px solid ${line}`,
        borderRadius: 2,
        backgroundColor: isDark ? alpha('#fff', 0.02) : '#f4f6f8',
        animation: 'channelMappingFade 180ms ease-out',
        '@keyframes channelMappingFade': {
          from: { opacity: 0.55 },
          to: { opacity: 1 },
        },
      }}
    >
      <Box
        sx={{
          width: ROOM_COL_WIDTH,
          flexShrink: 0,
          backgroundColor: surface,
        }}
      >
        <Box sx={{ height: CHANNEL_HEAD_HEIGHT, px: 2, py: 1.75, borderBottom: `1px solid ${line}` }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: text }}>Room types</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
            <CountChip
              isDark={isDark}
              icon={<CategoryOutlinedIcon />}
              label={`${roomRows.length} type${roomRows.length === 1 ? '' : 's'}`}
            />
            <CountChip
              isDark={isDark}
              accent
              icon={<LocalOfferOutlinedIcon />}
              label={`${totalRatePlans} rate plan${totalRatePlans === 1 ? '' : 's'}`}
            />
          </Box>
        </Box>
        {roomRows.map(({ roomType, plans, plansLoaded, plansLoading, ratePlanCount }) => {
          const open = Boolean(expandedRooms[String(roomType.id)]);
          const roomsInType = roomTypeCount(roomType);
          const skeletonCount = Math.max(ratePlanCount || 4, 3);
          return (
            <Box key={roomType.id}>
              <Box
                onClick={(event) => toggleRoom(roomType.id, event)}
                sx={{
                  height: ROOM_ROW_HEIGHT,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  borderBottom: `1px solid ${alpha(line, 0.85)}`,
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1, pr: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: text, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {roomType.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={roomType.code || roomSlug(roomType) || 'No code'}
                      sx={{
                        height: 20,
                        flexShrink: 0,
                        fontSize: '0.62rem',
                        fontWeight: 800,
                        letterSpacing: 0.3,
                        bgcolor: isDark ? alpha('#fff', 0.08) : '#eef2f7',
                        color: isDark ? '#e2e8f0' : '#334155',
                        border: `1px solid ${isDark ? alpha('#fff', 0.12) : '#d7dee8'}`,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.55 }}>
                    {roomsInType != null ? (
                      <CountChip
                        isDark={isDark}
                        icon={<MeetingRoomOutlinedIcon />}
                        label={`${roomsInType} room${roomsInType === 1 ? '' : 's'}`}
                      />
                    ) : null}
                    <CountChip
                      isDark={isDark}
                      accent
                      icon={<LocalOfferOutlinedIcon />}
                      label={`${ratePlanCount} rate${ratePlanCount === 1 ? '' : 's'}`}
                    />
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  aria-label={open ? 'Hide rates' : 'View rates'}
                  sx={{
                    color: ACCENT,
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    pointerEvents: 'none',
                  }}
                >
                  {open ? <KeyboardArrowUpIcon sx={{ fontSize: 20 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              </Box>
              {open
                ? (plansLoading || !plansLoaded
                    ? Array.from({ length: skeletonCount }, (_, index) => (
                        <RateRowSkeleton key={`${roomType.id}-sk-${index}`} isDark={isDark} line={line} />
                      ))
                    : (plans.length
                        ? plans.map((plan) => (
                            <Box
                              key={plan.id}
                              sx={{
                                height: RATE_ROW_HEIGHT,
                                px: 2.25,
                                display: 'flex',
                                alignItems: 'center',
                                borderBottom: `1px solid ${alpha(line, 0.6)}`,
                                backgroundColor: isDark ? alpha('#fff', 0.02) : '#f8fafb',
                              }}
                            >
                              <Typography sx={{ fontSize: '0.72rem', color: muted }} noWrap>
                                {plan.name}
                              </Typography>
                            </Box>
                          ))
                        : (
                          <Box
                            sx={{
                              height: RATE_ROW_HEIGHT,
                              px: 2.25,
                              display: 'flex',
                              alignItems: 'center',
                              borderBottom: `1px solid ${alpha(line, 0.6)}`,
                              backgroundColor: isDark ? alpha('#fff', 0.02) : '#f8fafb',
                            }}
                          >
                            <Typography sx={{ fontSize: '0.72rem', color: muted }}>
                              No rate plans for this room
                            </Typography>
                          </Box>
                        )))
                : null}
            </Box>
          );
        })}
      </Box>

      <Box sx={channelScrollPaneSx(isDark, line)}>
      {boardChannels.map((channel) => {
        const strip = channelStripColor(channel.name);
        const channelActive = allMappingsActive(channel.mappings);
        return (
          <Box
            key={channel.id}
            sx={{
              width: CHANNEL_COL_WIDTH,
              flexShrink: 0,
              backgroundColor: surface,
              borderRight: `1px solid ${line}`,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                height: CHANNEL_HEAD_HEIGHT,
                borderBottom: `1px solid ${line}`,
                position: 'relative',
                px: 1.5,
                pt: 1.75,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.6,
                opacity: channel.mappings.length && !channelActive ? 0.72 : 1,
              }}
            >
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, backgroundColor: strip }} />
              <ChannelImage channel={channel} size={42} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: text, textAlign: 'center' }} noWrap>
                {channel.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <MappingStatusButton
                  active={channelActive}
                  disabled={!channel.mappings.length || !onToggleMappings || isPending(channel.mappings)}
                  title={channelActive ? 'Turn off all mappings' : 'Turn on all mappings'}
                  onToggle={(nextActive) => onToggleMappings(channel.mappings, nextActive)}
                />
                <Tooltip title="Remove all mappings for this channel">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!channel.mappings.length}
                      onClick={() => onDeleteChannel({ channel, mappings: channel.mappings })}
                      sx={{
                        color: 'error.main',
                        width: 28,
                        height: 28,
                        '&:hover': { bgcolor: alpha('#ef4444', 0.12) },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {roomRows.map(({ roomType, plans, plansLoaded, plansLoading, ratePlanCount }) => {
              const open = Boolean(expandedRooms[String(roomType.id)]);
              const rows = mappingsByChannelRoom[`${channel.id}:${roomType.id}`] || [];
              const roomCode = rows[0]?.channel_room_code || '';
              const mapped = rows.length > 0;
              const skeletonCount = Math.max(ratePlanCount || 4, 3);
              return (
                <Box key={`${channel.id}-${roomType.id}`}>
                  <Box
                    onClick={(event) => toggleRoom(roomType.id, event)}
                    sx={{
                      height: ROOM_ROW_HEIGHT,
                      px: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom: `1px solid ${alpha(line, 0.85)}`,
                      cursor: 'pointer',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        minHeight: 40,
                        px: 1.1,
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 1,
                        border: `1px solid ${mapped ? alpha(ACCENT, 0.35) : line}`,
                        backgroundColor: mapped
                          ? alpha(ACCENT, isDark ? 0.12 : 0.08)
                          : isDark
                            ? alpha('#fff', 0.03)
                            : '#fafbfc',
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          fontSize: '0.74rem',
                          fontWeight: mapped ? 700 : 500,
                          color: mapped ? text : muted,
                        }}
                      >
                        {roomCode || 'Select room code'}
                      </Typography>
                    </Box>
                  </Box>
                  {open
                    ? (plansLoading || !plansLoaded
                        ? Array.from({ length: skeletonCount }, (_, index) => (
                            <RateRowSkeleton key={`${channel.id}-${roomType.id}-sk-${index}`} isDark={isDark} line={line} />
                          ))
                    : (plans.length
                        ? plans.map((plan) => {
                            const match = rows.find((row) => String(row.rate_plan_id) === String(plan.id));
                            const rateActive = mappingIsActive(match);
                            return (
                              <Box
                                key={`${channel.id}-${roomType.id}-${plan.id}`}
                                onClick={() => onOpenChannel(channel, match || rows[0] || null)}
                                sx={{
                                  height: RATE_ROW_HEIGHT,
                                  px: 1.25,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  borderBottom: `1px solid ${alpha(line, 0.6)}`,
                                  backgroundColor: isDark ? alpha('#fff', 0.02) : '#f8fafb',
                                  cursor: 'pointer',
                                  opacity: match && !rateActive ? 0.72 : 1,
                                }}
                              >
                                <Typography
                                  noWrap
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontSize: '0.7rem',
                                    color: match ? text : muted,
                                    fontWeight: match ? 600 : 500,
                                  }}
                                >
                                  {match?.channel_rate_code || 'Select rate code'}
                                </Typography>
                                {match ? (
                                  <MappingStatusButton
                                    active={rateActive}
                                    disabled={!onToggleMappings || isPending([match])}
                                    onToggle={(nextActive) => onToggleMappings([match], nextActive)}
                                  />
                                ) : null}
                              </Box>
                            );
                          })
                        : (
                          <Box
                            sx={{
                              height: RATE_ROW_HEIGHT,
                              px: 1.25,
                              display: 'flex',
                              alignItems: 'center',
                              borderBottom: `1px solid ${alpha(line, 0.6)}`,
                              backgroundColor: isDark ? alpha('#fff', 0.02) : '#f8fafb',
                            }}
                          />
                        )))
                    : null}
                </Box>
              );
            })}

          </Box>
        );
      })}
      </Box>
    </Box>
  );
}
